import {
  useState, useEffect, useCallback, useRef,
} from "react";
import { useSelector } from "react-redux";
import { AdminNotificationsService } from "@/shared/services/adminNotifications.service";
import { subscribe, unsubscribe } from "@/shared/services/pusher.service";
import type { AdminNotificationCounts } from "@/shared/types/adminNotifications.types";
import { RootState } from "@/store";

const DEFAULT_COUNTS: AdminNotificationCounts = {
  discussions_a_traiter: 0,
  discussions_a_traiter_only: 0,
  discussions_traitement_requis: 0,
  submissions_pending: 0,
  audit_logs_total: 0,
  communication_unread: 0,
  refund_requests_pending: 0,
  ecom_orders_pending: 0,
  catalog_bookings_pending: 0,
  classified_ads_pending: 0,
  classified_ads_reported: 0,
  beneficiaries_pending_validation: 0,
  proof_documents_pending: 0,
  legal_questions_unread: 0,
};

const CHANNEL_PREFIX = "admin_notifications_";

export const useAdminNotifications = () => {
  const [counts, setCounts] = useState<AdminNotificationCounts>(DEFAULT_COUNTS);
  const [isLoading, setIsLoading] = useState(true);
  const latestRequestRef = useRef(0);

  const organizationId = useSelector(
    (state: RootState) => state.session.user?.representative?.organization_id,
  );

  const fetchCounts = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    try {
      const data = await AdminNotificationsService.getCounts();
      // Des refetch rapprochés se chevauchent : une réponse plus ancienne arrivée
      // en dernier rétablirait un badge périmé.
      if (requestId !== latestRequestRef.current) return;
      // La réponse n'est pas normalisée : une clé absente arriverait `undefined`,
      // et NotificationBadge teste `count <= 0`, donc rendrait une pastille vide.
      setCounts({ ...DEFAULT_COUNTS, ...data });
    }
    catch (_error) {
      // Silent fail - notification counts are non-critical
    }
    finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  // Pusher subscription
  useEffect(() => {
    if (!organizationId) return;

    const channelName = `${CHANNEL_PREFIX}${organizationId}`;

    const handleCountsUpdated = (data: AdminNotificationCounts) => {
      setCounts((prev) => ({
        ...prev,
        discussions_a_traiter: data.discussions_a_traiter ?? prev.discussions_a_traiter,
        discussions_a_traiter_only: data.discussions_a_traiter_only ?? prev.discussions_a_traiter_only,
        discussions_traitement_requis: data.discussions_traitement_requis ?? prev.discussions_traitement_requis,
        submissions_pending: data.submissions_pending ?? prev.submissions_pending,
        audit_logs_total: data.audit_logs_total ?? prev.audit_logs_total,
        communication_unread: data.communication_unread ?? prev.communication_unread,
        refund_requests_pending: data.refund_requests_pending ?? prev.refund_requests_pending,
        ecom_orders_pending: data.ecom_orders_pending ?? prev.ecom_orders_pending,
        catalog_bookings_pending: data.catalog_bookings_pending ?? prev.catalog_bookings_pending,
        classified_ads_pending: data.classified_ads_pending ?? prev.classified_ads_pending,
        classified_ads_reported: data.classified_ads_reported ?? prev.classified_ads_reported,
        beneficiaries_pending_validation: data.beneficiaries_pending_validation ?? prev.beneficiaries_pending_validation,
        proof_documents_pending: data.proof_documents_pending ?? prev.proof_documents_pending,
        legal_questions_unread: data.legal_questions_unread ?? prev.legal_questions_unread,
      }));
    };

    subscribe(channelName, "counts_updated", handleCountsUpdated);

    return () => {
      unsubscribe(channelName, "counts_updated", handleCountsUpdated);
    };
  }, [organizationId]);

  return { counts, isLoading, refetch: fetchCounts };
};
